/**
 * Single source of truth for HTTP API route paths. Both the SPA API client and
 * the CDK ApiStack route definitions import from here so they never drift.
 * Paths are mounted under the HTTP API Gateway stage base URL configured per
 * env in `infra/config/{env}.ts`.
 */
export const API_ROUTES = {
  // --- auth (public) ---
  signup: { method: 'POST', path: '/auth/signup' },
  login: { method: 'POST', path: '/auth/login' },
  verifyEmail: { method: 'POST', path: '/auth/verify' },
  forgotPassword: { method: 'POST', path: '/auth/forgot-password' },
  resetPassword: { method: 'POST', path: '/auth/reset-password' },
  refresh: { method: 'POST', path: '/auth/refresh' },
  logout: { method: 'POST', path: '/auth/logout' },
  mfaVerify: { method: 'POST', path: '/auth/mfa/verify' },

  // --- account (authenticated) ---
  me: { method: 'GET', path: '/me' },
  updateProfile: { method: 'PATCH', path: '/me/profile' },
  updatePreferences: { method: 'PATCH', path: '/me/preferences' },
  changePassword: { method: 'POST', path: '/me/password' },
  mfaSetupBegin: { method: 'POST', path: '/me/mfa/setup' },
  mfaSetupVerify: { method: 'POST', path: '/me/mfa/verify' },
  mfaDisable: { method: 'POST', path: '/me/mfa/disable' },
  exportData: { method: 'POST', path: '/me/export' },
  deleteAccount: { method: 'POST', path: '/me/delete' },
  // device-capability telemetry (CA-002; FEATURES.deviceTelemetry — 404 when off)
  deviceTelemetry: { method: 'POST', path: '/me/device-telemetry' },

  // --- billing (authenticated; Stripe stub this session) ---
  billingCheckout: { method: 'POST', path: '/billing/checkout' },
  billingPortal: { method: 'POST', path: '/billing/portal' },
  stripeWebhook: { method: 'POST', path: '/webhooks/stripe' },

  // --- games (authenticated; FEATURES.gameCreation) ---
  createGame: { method: 'POST', path: '/games' },
  listGames: { method: 'GET', path: '/games' },
  getGame: { method: 'GET', path: '/games/{gameId}' },
  updateGame: { method: 'PATCH', path: '/games/{gameId}' },
  archiveGame: { method: 'POST', path: '/games/{gameId}/archive' },
  templateTaxonomy: { method: 'GET', path: '/games/template/taxonomy' },
  // game-dev jobs (authenticated; FEATURES.gameDevBackend — Cycle-2 D-062)
  createJob: { method: 'POST', path: '/games/{gameId}/jobs' },
  listJobs: { method: 'GET', path: '/games/{gameId}/jobs' },
  getJob: { method: 'GET', path: '/games/{gameId}/jobs/{jobId}' },
  cancelJob: { method: 'POST', path: '/games/{gameId}/jobs/{jobId}/cancel' },

  // version actions (VT-001..VT-004; FEATURES.versionActions — versions are
  // CREATED only by the result pipeline, so the user verbs are exactly these;
  // the old createVersion 501 stub is retired)
  restoreVersion: { method: 'POST', path: '/games/{gameId}/versions/{versionId}/restore' },
  branchVersion: { method: 'POST', path: '/games/{gameId}/versions/{versionId}/branch' },
  archiveVersion: { method: 'POST', path: '/games/{gameId}/versions/{versionId}/archive' },
  unarchiveVersion: { method: 'POST', path: '/games/{gameId}/versions/{versionId}/unarchive' },
  playVersion: { method: 'GET', path: '/games/{gameId}/versions/{versionId}/play' },
  publishGame: { method: 'POST', path: '/games/{gameId}/publish' },
  unpublishGame: { method: 'POST', path: '/games/{gameId}/unpublish' },
  resolvePlay: { method: 'GET', path: '/play/{slug}' },
  resolvePlayUrl: { method: 'GET', path: '/play/{slug}/url' },
  shareGame: { method: 'POST', path: '/games/{gameId}/share' },
  sharedWithMe: { method: 'GET', path: '/games/shared-with-me' },
  listShares: { method: 'GET', path: '/games/{gameId}/shares' },
  revokeShare: { method: 'POST', path: '/games/{gameId}/shares/{shareId}/revoke' },

  // --- friends / connections (authenticated; FEATURES.friends) ---
  listConnections: { method: 'GET', path: '/friends' },
  sendFriendRequest: { method: 'POST', path: '/friends/requests' },
  acceptFriendRequest: { method: 'POST', path: '/friends/requests/{otherSub}/accept' },
  declineFriendRequest: { method: 'POST', path: '/friends/requests/{otherSub}/decline' },
  removeFriend: { method: 'POST', path: '/friends/{otherSub}/remove' },
  blockFriend: { method: 'POST', path: '/friends/{otherSub}/block' },

  // --- privacy / public ---
  dsar: { method: 'POST', path: '/dsar' },
  contact: { method: 'POST', path: '/contact' },

  // --- admin (OperatorAdmins group only) ---
  adminListUsers: { method: 'GET', path: '/admin/users' },
  adminGetUser: { method: 'GET', path: '/admin/users/{sub}' },
  adminSuspendUser: { method: 'POST', path: '/admin/users/{sub}/suspend' },
  adminResetUserPassword: { method: 'POST', path: '/admin/users/{sub}/reset-password' },
  adminCreateTestUser: { method: 'POST', path: '/admin/test-users' },
  adminListAlarms: { method: 'GET', path: '/admin/alarms' },
  adminListTickets: { method: 'GET', path: '/admin/tickets' },
  adminResolveTicket: { method: 'POST', path: '/admin/tickets/{ticketId}/resolve' },
  adminUsageMetrics: { method: 'GET', path: '/admin/metrics/usage' },
  adminTaxonomyInsights: { method: 'GET', path: '/admin/taxonomy-insights' },
  adminDeviceCapabilities: { method: 'GET', path: '/admin/device-capabilities' },
  adminSystemHealth: { method: 'GET', path: '/admin/health' },
} as const;

export type ApiRoute = (typeof API_ROUTES)[keyof typeof API_ROUTES];
